import { i as isLavOption } from './types.js';

/**
 * This module offers helper functions for components that display a list of options (variations on SelectInput)
 */
function isOtherOption(o) {
    return (typeof o === 'object' &&
        o !== null &&
        'type' in o &&
        (o.type === 'header' || o.type === 'divider' || o.type === 'meta' || o.type === 'button'));
}
/**
 * Construct Fuse instance for client-side filtering
 */
async function constructFuse(opts) {
    const options = (opts ?? []).filter((v) => isLavOption(v));
    const Fuse = (await import('./fuse.js')).default;
    return new Fuse(options, { keys: ['label'], shouldSort: true });
}
function isSearchable(cmp) {
    return Boolean('searchable' in cmp);
}
async function handleOptions(cmp, options) {
    if (options instanceof Promise) {
        // Resolve static options in Promise
        try {
            cmp.optionsResolved = await options;
        }
        catch (err) {
            // Can not handle
        }
    }
    else if (Array.isArray(options)) {
        // Resolve static options in array
        cmp.optionsResolved = [...options];
    }
    if (isSearchable(cmp)) {
        const areOptionsStatic = options instanceof Promise || Array.isArray(options);
        if (areOptionsStatic) {
            const allOptions = cmp.optionsResolved ? [...cmp.optionsResolved] : cmp.optionsResolved;
            // Construct fuse instance to search through static options
            const fuse = await constructFuse(cmp.optionsResolved);
            cmp.fuseSearch = (phrase) => {
                if (phrase.trim()) {
                    return fuse.search(phrase).map(({ item }) => item);
                }
                return allOptions;
            };
        }
        else {
            // Trigger search (based on current search query)
            await cmp.filterOptions();
        }
    }
}
async function useSearchFunction(cmp, search) {
    cmp.loading = true;
    // Cancel previous requests
    for (const c of cmp.abortControllers) {
        c.abort();
    }
    // Create abort controller for this request and cache it internally
    const abortController = new AbortController();
    cmp.abortControllers.push(abortController);
    try {
        // Request data and set results to filtered options
        const results = await search(cmp.searchQuery, cmp.value, abortController);
        return results;
    }
    catch (err) {
        // There's nothing we can do with this error
    }
    finally {
        cmp.loading = false;
        // Remove this abortController from our internal state because we don't
        // need it any more: the request is finished!
        cmp.abortControllers = cmp.abortControllers.filter((a) => a !== abortController);
    }
}
function getFilterOptionsFn(cmp) {
    return async () => {
        if (typeof cmp.options === 'function') {
            // Use custom async search function
            cmp.optionsResolved = await useSearchFunction(cmp, cmp.options);
        }
        else if (cmp.fuseSearch) {
            // Use client-side search to filter only our current options
            cmp.optionsResolved = cmp.fuseSearch(cmp.searchQuery);
        }
    };
}

export { getFilterOptionsFn as g, handleOptions as h, isOtherOption as i };

//# sourceMappingURL=index6.js.map